import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, MouseEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  applyNodeChanges,
  MarkerType,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from "@xyflow/react";

import { useArchitectureWorkspace } from "@/application/use-cases/architecture/use-architecture-workspace.queries";
import { useMapWorkspace } from "@/application/use-cases/architecture/use-map-workspace.queries";
import {
  parseWorkspaceStageHighlights,
  stageHighlightsToJson,
  type WorkspaceStageHighlights,
} from "@/infrastructure/repositories/architecture-workspace.mappers";
import {
  type ArchitectureMapEdge,
  type ArchitectureMapEdgeKind,
  type ArchitectureMapNode,
  type MapDomain,
  STAGE_DESCRIPTION,
  STAGE_HEADLINE,
  STAGE_LABEL,
  STAGES,
  type Stage,
} from "@/presentation/data/architecture-map-data";

export type MapNodeData = ArchitectureMapNode & {
  active: boolean;
  onToggleHighlight: (id: string) => void;
  /** When provided, the node label becomes double-click-editable. */
  onRename?: (id: string, nextLabel: string) => void;
} & Record<string, unknown>;

interface ArchitectureMapNavigate {
  (opts: { search: { stage: Stage } }): void;
}

/** Manual z-index layer: cards above edges by default (`zIndexMode="manual"` on ReactFlow). */
const MAP_NODE_Z = 10;
const MAP_EDGE_Z_DEFAULT = 0;
const MAP_EDGE_Z_SPOTLIGHT = 28;

type StageHighlights = WorkspaceStageHighlights;

function presetActiveIds(stage: Stage, nodes: readonly ArchitectureMapNode[]): Set<string> {
  const set = new Set<string>();
  for (const c of nodes) {
    if (c.stages.includes(stage)) set.add(c.id);
  }
  return set;
}

interface EdgePaint {
  style: CSSProperties;
  /** Solid color for arrowheads (SVG markers ignore stroke alpha). */
  markerColor: string;
}

function edgePaint(kind: ArchitectureMapEdgeKind, bothActive: boolean): EdgePaint {
  const dim = !bothActive;
  const a = dim ? 0.32 : 0.86;
  switch (kind) {
    case "finance":
      return {
        markerColor: "oklch(0.48 0.12 72)",
        style: {
          stroke: `oklch(0.55 0.13 75 / ${a})`,
          strokeWidth: bothActive ? 2.75 : 1.45,
        },
      };
    case "feedback":
      return {
        markerColor: "oklch(0.46 0.17 300)",
        style: {
          stroke: `oklch(0.52 0.18 300 / ${a})`,
          strokeWidth: bothActive ? 2.5 : 1.45,
          strokeDasharray: dim ? "6 8" : "8 6",
        },
      };
    case "manual":
      return {
        markerColor: "oklch(0.4 0.1 145)",
        style: {
          stroke: `oklch(0.48 0.11 145 / ${a})`,
          strokeWidth: bothActive ? 2.2 : 1.3,
          strokeDasharray: "6 6",
        },
      };
    case "bidirectional":
      return {
        markerColor: "oklch(0.44 0.13 200)",
        style: {
          stroke: `oklch(0.5 0.15 200 / ${a})`,
          strokeWidth: bothActive ? 2.5 : 1.45,
          strokeDasharray: "3 4",
        },
      };
    case "dependency":
      return {
        markerColor: "oklch(0.4 0.02 250)",
        style: {
          stroke: `oklch(0.45 0.03 250 / ${dim ? 0.4 : 0.6})`,
          strokeWidth: 1.25,
          strokeDasharray: "2 8",
        },
      };
    default:
      return {
        markerColor: "oklch(0.4 0.16 195)",
        style: {
          stroke: `oklch(0.52 0.16 190 / ${a})`,
          strokeWidth: bothActive ? 2.5 : 1.45,
        },
      };
  }
}

function applyLinkFocus(
  focusId: string | null,
  source: string,
  target: string,
  paint: EdgePaint
): EdgePaint {
  if (focusId === null) return paint;
  const hit = source === focusId || target === focusId;
  const w = Number(paint.style.strokeWidth ?? 1.5);
  if (hit) {
    return {
      markerColor: paint.markerColor,
      style: {
        ...paint.style,
        strokeWidth: w + 1.5,
        opacity: 1,
      },
    };
  }
  return {
    markerColor: "oklch(0.74 0.01 240)",
    style: {
      ...paint.style,
      strokeWidth: Math.max(0.75, w * 0.88),
      opacity: 0.1,
    },
  };
}

function makeXyNode(
  c: ArchitectureMapNode,
  active: boolean,
  onToggleHighlight: (id: string) => void,
  onRename: (id: string, next: string) => void
): Node<MapNodeData> {
  return {
    id: c.id,
    type: "mapComponent",
    position: c.position,
    zIndex: MAP_NODE_Z,
    data: { ...c, active, onToggleHighlight, onRename },
    draggable: true,
    selectable: true,
  };
}

export function useArchitectureMapPage(stage: Stage) {
  const navigate = useNavigate({ from: "/architecture/map" }) as unknown as ArchitectureMapNavigate;

  const { workspace, patch } = useArchitectureWorkspace();
  const {
    nodes: dataNodes,
    edges: dataEdges,
    setNodes: persistNodes,
    setEdges: persistEdges,
    resetToSeed,
  } = useMapWorkspace();

  // Refs for stable mutation paths
  const dataNodesRef = useRef(dataNodes);
  const dataEdgesRef = useRef(dataEdges);
  useEffect(() => {
    dataNodesRef.current = dataNodes;
  }, [dataNodes]);
  useEffect(() => {
    dataEdgesRef.current = dataEdges;
  }, [dataEdges]);

  const [stageOverrides, setStageOverrides] = useState<StageHighlights>({});
  const stageRef = useRef(stage);
  const stageOverridesRef = useRef(stageOverrides);

  useEffect(() => {
    stageRef.current = stage;
  }, [stage]);
  useEffect(() => {
    stageOverridesRef.current = stageOverrides;
  }, [stageOverrides]);

  const activeIds = useMemo(() => {
    const saved = stageOverrides[stage];
    return saved ? new Set(saved) : presetActiveIds(stage, dataNodes);
  }, [stage, stageOverrides, dataNodes]);

  const hasStageOverride = stageOverrides[stage] !== undefined;

  const [linkFocusId, setLinkFocusId] = useState<string | null>(null);
  const [dialogNodeId, setDialogNodeId] = useState<string | null>(null);

  // Reset transient UI selections when the user switches stages.
  /* eslint-disable react-hooks/set-state-in-effect -- stage-change cleanup */
  useEffect(() => {
    setLinkFocusId(null);
    setDialogNodeId(null);
  }, [stage]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const toggleHighlight = useCallback(
    (id: string) => {
      const currentStage = stageRef.current;
      setStageOverrides((current) => {
        const baseList = current[currentStage];
        const baseSet = baseList
          ? new Set(baseList)
          : presetActiveIds(currentStage, dataNodesRef.current);
        if (baseSet.has(id)) baseSet.delete(id);
        else baseSet.add(id);
        const next: StageHighlights = { ...current, [currentStage]: Array.from(baseSet) };
        patch({ map_stage_highlights: stageHighlightsToJson(next) });
        return next;
      });
    },
    [patch]
  );

  // Rename: update the node's name in the workspace.
  const renameNodeRef = useRef<(id: string, next: string) => void>(() => {});
  const renameNode = useCallback(
    (id: string, nextName: string) => {
      const updated = dataNodesRef.current.map((n) => (n.id === id ? { ...n, name: nextName } : n));
      dataNodesRef.current = updated;
      persistNodes(updated);
    },
    [persistNodes]
  );
  useEffect(() => {
    renameNodeRef.current = renameNode;
  }, [renameNode]);

  const stableRename = useCallback((id: string, next: string) => {
    renameNodeRef.current(id, next);
  }, []);

  /** xyflow node state, hydrated from the workspace. */
  const [xyNodes, setXyNodes] = useState<Node<MapNodeData>[]>([]);
  const hydratedRef = useRef(false);

  // Hydrate xyflow nodes whenever the workspace data identity changes (initial
  // load, Reset, remote update). The dependency on activeIds also rehydrates
  // when the stage or highlight overrides change so the `active` flag stays in
  // sync without a separate effect.
  /* eslint-disable react-hooks/set-state-in-effect -- controlled xyflow bridge */
  useEffect(() => {
    setXyNodes(
      dataNodes.map((c) => makeXyNode(c, activeIds.has(c.id), toggleHighlight, stableRename))
    );
    hydratedRef.current = true;
  }, [dataNodes, activeIds, toggleHighlight, stableRename]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setXyNodes((current) => applyNodeChanges(changes, current) as Node<MapNodeData>[]);
      let positionDirty = false;
      const removeIds: string[] = [];
      const nextPositionsById: Record<string, { x: number; y: number }> = {};
      for (const ch of changes) {
        if (ch.type === "position" && ch.position && ch.dragging === false) {
          nextPositionsById[ch.id] = ch.position;
          positionDirty = true;
        }
        if (ch.type === "remove") {
          removeIds.push(ch.id);
        }
      }
      if (positionDirty) {
        const next: ArchitectureMapNode[] = dataNodesRef.current.map((n) => {
          const pos = nextPositionsById[n.id];
          return pos ? { ...n, position: pos } : n;
        });
        dataNodesRef.current = next;
        persistNodes(next);
      }
      if (removeIds.length > 0) {
        const idSet = new Set(removeIds);
        const nextNodes = dataNodesRef.current.filter((n) => !idSet.has(n.id));
        const nextEdges = dataEdgesRef.current.filter(
          (e) => !idSet.has(e.source) && !idSet.has(e.target)
        );
        dataNodesRef.current = nextNodes;
        dataEdgesRef.current = nextEdges;
        persistNodes(nextNodes);
        persistEdges(nextEdges);
      }
    },
    [persistNodes, persistEdges]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const removeIds = changes.filter((c) => c.type === "remove").map((c) => c.id);
      if (removeIds.length > 0) {
        const idSet = new Set(removeIds);
        const nextEdges = dataEdgesRef.current.filter((e) => !idSet.has(e.id));
        dataEdgesRef.current = nextEdges;
        persistEdges(nextEdges);
      }
    },
    [persistEdges]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      if (connection.source === connection.target) return;
      const edgeId = `e-${connection.source}-${connection.target}-${Date.now().toString(36)}`;
      const next: ArchitectureMapEdge = {
        id: edgeId,
        source: connection.source,
        target: connection.target,
        kind: "primary",
      };
      const nextEdges = [...dataEdgesRef.current, next];
      dataEdgesRef.current = nextEdges;
      persistEdges(nextEdges);
    },
    [persistEdges]
  );

  const addNode = useCallback(
    (input: {
      name: string;
      role: string;
      domain: MapDomain;
      kind: ArchitectureMapNode["kind"];
      ownership: ArchitectureMapNode["ownership"];
    }) => {
      const id = `${input.kind === "actor" ? "actor" : "node"}-${Date.now().toString(36)}`;
      // Drop the node at a non-colliding offset
      const baseX = 1200;
      const baseY = 600;
      const occupiedYs = dataNodesRef.current
        .filter((n) => Math.abs(n.position.x - baseX) < 100)
        .map((n) => n.position.y);
      let y = baseY;
      while (occupiedYs.some((oy) => Math.abs(oy - y) < 160)) y += 180;
      const next: ArchitectureMapNode = {
        id,
        name: input.name,
        role: input.role,
        kind: input.kind,
        ownership: input.ownership,
        domain: input.domain,
        stages: [...STAGES],
        meetings: [],
        position: { x: baseX, y },
      };
      const nextNodes = [...dataNodesRef.current, next];
      dataNodesRef.current = nextNodes;
      persistNodes(nextNodes);
    },
    [persistNodes]
  );

  // Initial hydration from workspace (highlights only — nodes/edges come from useMapWorkspace).
  /* eslint-disable react-hooks/set-state-in-effect -- controlled bridge from query data */
  useEffect(() => {
    if (!workspace) return;
    const highlights = parseWorkspaceStageHighlights(workspace.map_stage_highlights);
    setStageOverrides(highlights);
  }, [workspace]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const onNodeClick = useCallback((event: MouseEvent, node: Node) => {
    const data = node.data as MapNodeData | undefined;
    if (event.altKey) {
      setLinkFocusId((cur) => (cur === node.id ? null : node.id));
      return;
    }
    if (data?.kind === "actor") {
      setLinkFocusId((cur) => (cur === node.id ? null : node.id));
      return;
    }
    setDialogNodeId(node.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setLinkFocusId(null);
  }, []);

  const closeDialog = useCallback(() => setDialogNodeId(null), []);

  const resetLayout = useCallback(() => {
    resetToSeed();
    setStageOverrides({});
    patch({ map_stage_highlights: stageHighlightsToJson({}) });
  }, [patch, resetToSeed]);

  const resetStageHighlights = useCallback(() => {
    const currentStage = stageRef.current;
    setStageOverrides((current) => {
      if (current[currentStage] === undefined) return current;
      const next = { ...current };
      delete next[currentStage];
      patch({ map_stage_highlights: stageHighlightsToJson(next) });
      return next;
    });
  }, [patch]);

  const edges = useMemo<Edge[]>(() => {
    return dataEdges.map((e) => {
      const bothActive = activeIds.has(e.source) && activeIds.has(e.target);
      const raw = edgePaint(e.kind, bothActive);
      const paint = applyLinkFocus(linkFocusId, e.source, e.target, raw);
      const isIncident =
        linkFocusId === null || e.source === linkFocusId || e.target === linkFocusId;
      const labelMuted = linkFocusId !== null && !isIncident;
      const markerCommon = {
        type: MarkerType.ArrowClosed,
        width: 24,
        height: 24,
        color: paint.markerColor,
      } as const;
      const hitFocus = linkFocusId !== null && isIncident;

      return {
        id: e.id,
        source: e.source,
        target: e.target,
        type: "default",
        label: e.label,
        style: paint.style,
        zIndex:
          linkFocusId === null
            ? MAP_EDGE_Z_DEFAULT
            : hitFocus
              ? MAP_EDGE_Z_SPOTLIGHT
              : MAP_EDGE_Z_DEFAULT,
        markerEnd: markerCommon,
        markerStart: e.kind === "bidirectional" ? markerCommon : undefined,
        labelStyle: {
          fontSize: 11,
          fontFamily: "var(--font-mono)",
          fontWeight: 700,
          fill: labelMuted
            ? "oklch(0.52 0.02 240 / 0.12)"
            : bothActive
              ? "oklch(0.3 0.14 200)"
              : "oklch(0.44 0.03 240 / 0.55)",
        },
        labelBgStyle: {
          fill: "oklch(1 0 0)",
          fillOpacity: labelMuted ? 0.35 : 0.96,
        },
        labelBgPadding: [8, 4] as [number, number],
        labelBgBorderRadius: 5,
      };
    });
  }, [dataEdges, activeIds, linkFocusId]);

  function selectStage(next: Stage) {
    navigate({ search: { stage: next } });
  }

  const activeCount = activeIds.size;
  const totalCount = dataNodes.length;
  const yandexActiveCount = dataNodes.filter(
    (c) => c.kind === "system" && c.ownership === "yandex" && activeIds.has(c.id)
  ).length;

  return {
    stage,
    selectStage,
    nodes: xyNodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onNodeClick,
    onPaneClick,
    resetLayout,
    hasOverrides: dataNodes !== undefined && dataNodes.length > 0, // always has data once loaded
    resetStageHighlights,
    hasStageOverride,
    activeCount,
    totalCount,
    yandexActiveCount,
    stageLabel: STAGE_LABEL[stage],
    stageHeadline: STAGE_HEADLINE[stage],
    stageDescription: STAGE_DESCRIPTION[stage],
    stages: STAGES,
    dialogNodeId,
    closeDialog,
    addNode,
    renameNode,
  };
}
